import React from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';
import { useCaloricInputs } from './CalorieCalc';

const ACT = [
  { value: 1.2, label: 'Sedentary: little or no exercise' },
  { value: 1.375, label: 'Light: exercise 1-3 times/week' },
  { value: 1.465, label: 'Moderate: exercise 4-5 times/week' },
  { value: 1.55, label: 'Active: daily exercise or intense exercise 3-4 times/week' },
  { value: 1.725, label: 'Very Active: intense exercise 6-7 times/week' },
  { value: 1.9, label: 'Extra Active: very intense exercise daily, or physical job' },
];

export default function TDEECalc() {
  const s = useCaloricInputs();
  const heightInputs = s.tab === 0
    ? <div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={s.ft} inches={s.inches} onFt={s.setFt} onIn={s.setInches} testidPrefix="td-h" /></div>
    : <UnitInput value={s.cm} onChange={s.setCm} unit="cm" testid="td-h-cm" />;
  const weightInput = s.tab === 0
    ? <UnitInput value={s.lb} onChange={s.setLb} unit="pounds" testid="td-w-lb" />
    : <UnitInput value={s.kg} onChange={s.setKg} unit="kg" testid="td-w-kg" />;

  return (
    <CalcShell look="cnet" title="TDEE Calculator" subtitle="Estimate your Total Daily Energy Expenditure (TDEE).">
      <CalcNetPanel
        slug="td"
        activeTab={s.tab}
        onTabChange={s.setTab}
        onClear={s.clear}
        settings={
          <>
            <Row label="BMR Formula">
              <select value={s.formula} onChange={e => s.setFormula(e.target.value)} data-testid="td-formula" className="w-full px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
                <option value="mifflin">Mifflin-St Jeor</option>
                <option value="harris">Revised Harris-Benedict</option>
                <option value="katch">Katch-McArdle</option>
              </select>
            </Row>
            {s.formula === 'katch' && <Row label="Body Fat"><UnitInput value={s.bodyFat} onChange={s.setBodyFat} unit="%" testid="td-bf" /></Row>}
          </>
        }
        right={
          <div>
            <div className="text-lg font-semibold text-slate-800 mb-1">Total Daily Energy Expenditure</div>
            <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="td-tdee">
              {Math.round(s.tdee).toLocaleString()} Calories/day
            </div>
            <p className="text-sm text-slate-600 mt-1">BMR ({Math.round(s.bmr).toLocaleString()}) × activity ({s.activity})</p>
            <div className="mt-4">
              <ResultTable
                testid="td-macro-table"
                headers={['', 'Macronutrient', 'Grams', 'Percent']}
                rows={[
                  ['Protein', 'Protein', `${Math.round(s.tdee * 0.3 / 4)} g`, '30%'],
                  ['Carbs', 'Carbohydrate', `${Math.round(s.tdee * 0.4 / 4)} g`, '40%'],
                  ['Fat', 'Fat', `${Math.round(s.tdee * 0.3 / 9)} g`, '30%'],
                ]}
              />
            </div>
            <div className="mt-4">
              <h4 className="font-bold text-slate-900 mb-2 text-sm">Weight-Loss / Weight-Gain Calorie Targets</h4>
              <table className="w-full text-sm border border-slate-300" data-testid="td-weight-table">
                <thead className="bg-[#1e40af] text-white">
                  <tr><th className="px-3 py-2 text-left">Goal</th><th className="px-3 py-2 text-right">Calorie Delta</th><th className="px-3 py-2 text-right">Target (cal/day)</th></tr>
                </thead>
                <tbody>
                  {[
                    ['Extreme weight loss (-1 kg / week)', -1000, 'text-red-700 font-bold'],
                    ['Weight loss (-0.5 kg / week)', -500, 'text-red-600 font-semibold'],
                    ['Mild weight loss (-0.25 kg / week)', -250, 'text-orange-600'],
                    ['Maintenance', 0, 'text-slate-700 font-bold'],
                    ['Mild weight gain (+0.25 kg / week)', +250, 'text-emerald-600'],
                    ['Weight gain (+0.5 kg / week)', +500, 'text-emerald-700 font-semibold'],
                  ].map(([label, delta, cls], i) => (
                    <tr key={i} className={i % 2 ? 'bg-slate-50' : ''}>
                      <td className={`px-3 py-1.5 ${cls}`}>{label}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{delta === 0 ? '—' : (delta > 0 ? '+' : '') + delta}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{Math.round(s.tdee + delta).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-slate-600">Above shows one common macro distribution. TDEE ≈ maintenance calories at current activity.</p>
          </div>
        }
      >
        <Row label="Age"><UnitInput value={s.age} onChange={s.setAge} testid="td-age" /><span className="text-slate-500 text-xs whitespace-nowrap">ages 18 – 80</span></Row>
        <Row label="Gender"><RadioGroup name="td-g" value={s.gender} onChange={s.setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]} /></Row>
        <Row label="Height">{heightInputs}</Row>
        <Row label="Weight">{weightInput}</Row>
        <Row label="Activity"><select value={s.activity} onChange={e => s.setActivity(+e.target.value)} className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" data-testid="td-activity">{ACT.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></Row>
      </CalcNetPanel>

      <StepsPanel steps={[
        `BMR (${s.formula}) ≈ ${Math.round(s.bmr).toLocaleString()} cal/day`,
        `TDEE = BMR × activity factor (${s.activity}) = <b>${Math.round(s.tdee).toLocaleString()} cal/day</b>`,
        `Use TDEE as maintenance calories; add/subtract to bulk/cut.`,
      ]} />
    </CalcShell>
  );
}
